import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { sectionRenderer } from '@theme/section-renderer';

/*
 * VELORINE PRICE-BASED REWARDS
 *
 * ₹499+    = free shipping
 * ₹699+    = free small ring
 * ₹1299+   = free statement earrings
 * ₹2000+   = free small heart dainty chain
 *
 * Only cart lines containing:
 * _velorine_free_gift: 'true'
 *
 * are treated as automatic free gifts.
 */

const VELORINE_REWARDS = [
  {
    min: 69900,
    max: 129899,
    handle: 'small-ring',
  },
  {
    min: 129900,
    max: 199999,
    handle: 'statement-earrings',
  },
  {
    min: 200000,
    max: 1009999,
    handle: 'small-heart-dainty-chain',
  },
];

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;
  #rewardSyncing = false;
  #rewardSyncTimer = null;
  #confettiTimer = null;

  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }

    this.#scheduleRewardSync(150);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#rewardSyncTimer) {
      clearTimeout(this.#rewardSyncTimer);
    }

    if (this.#confettiTimer) {
      clearTimeout(this.#confettiTimer);
    }
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector(
        'shopify-payment-terms'
      )?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
        } else {
          const openAndSettle = () => {
            if (
              !this.#themeDrawer?.isOpen &&
              !this.#isCartEmpty()
            ) {
              this.#themeDrawer?.open();
            }

            settle();
          };

          if (sourceModal?.open) {
            sourceModal.addEventListener(
              'close',
              openAndSettle,
              { once: true }
            );
          } else {
            openAndSettle();
          }
        }

        this.#scheduleRewardSync(100);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio = drawerHeight
      ? summaryHeight / drawerHeight
      : 1;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }

  /*
   * ONLY automatically generated gifts are excluded.
   *
   * A customer manually buying free-chain/free-earcuffs/
   * free-ring is NOT a reward line.
   */
  #isRewardLine(item) {
    return (
      item.properties?._velorine_free_gift === 'true'
    );
  }

  #getQualifyingTotal(cart) {
    return cart.items
      .filter((item) => !this.#isRewardLine(item))
      .reduce(
        (total, item) =>
          total + (item.final_line_price || 0),
        0
      );
  }

  #triggerMilestoneConfetti(total) {
    if (
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      window.__velorineRewardConfettiTotal = total;
      return;
    }

    const previous =
      window.__velorineRewardConfettiTotal;

    if (typeof previous !== 'number') {
      window.__velorineRewardConfettiTotal = total;
      return;
    }

    const milestones = [
      49900,
      69900,
      129900,
      200000,
    ];

    const crossed =
      total > previous
        ? milestones.filter(
            (milestone) =>
              previous < milestone &&
              total >= milestone
          )
        : [];

    window.__velorineRewardConfettiTotal = total;

    if (!crossed.length) return;

    crossed.forEach((milestone, index) => {
      this.#confettiTimer = setTimeout(
        () =>
          this.#burstMilestoneConfetti(
            milestone
          ),
        index * 450
      );
    });
  }

  #burstMilestoneConfetti(milestone) {
    const progress = this.querySelector(
      '[data-cart-rewards] .cart-rewards__progress'
    );

    const layer = progress?.querySelector(
      '[data-velorine-confetti]'
    );

    const milestoneEl = progress?.querySelector(
      `[data-reward-milestone="${milestone}"]`
    );

    if (!progress || !layer || !milestoneEl) {
      return;
    }

    const progressRect =
      progress.getBoundingClientRect();

    const milestoneRect =
      milestoneEl.getBoundingClientRect();

    const originX =
      milestoneRect.left +
      milestoneRect.width / 2 -
      progressRect.left;

    const originY = 14;

    milestoneEl.classList.remove(
      'velorine-milestone-pop'
    );

    void milestoneEl.offsetWidth;

    milestoneEl.classList.add(
      'velorine-milestone-pop'
    );

    const count = 42;

    for (let i = 0; i < count; i += 1) {
      const piece =
        document.createElement('span');

      piece.className =
        'velorine-confetti-piece';

      const angle =
        Math.random() * Math.PI * 2;

      const distance =
        35 + Math.random() * 105;

      const tx =
        Math.cos(angle) * distance;

      const ty =
        Math.sin(angle) * distance +
        35 +
        Math.random() * 45;

      piece.style.left =
        `${originX}px`;

      piece.style.top =
        `${originY}px`;

      piece.style.setProperty(
        '--tx',
        `${tx}px`
      );

      piece.style.setProperty(
        '--ty',
        `${ty}px`
      );

      piece.style.setProperty(
        '--rot',
        `${-360 + Math.random() * 720}deg`
      );

      piece.style.width =
        `${4 + Math.random() * 4}px`;

      piece.style.height =
        `${7 + Math.random() * 6}px`;

      piece.style.animationDelay =
        `${Math.random() * 100}ms`;

      layer.appendChild(piece);
    }

    setTimeout(() => {
      layer
        .querySelectorAll(
          '.velorine-confetti-piece'
        )
        .forEach((piece) => piece.remove());

      milestoneEl.classList.remove(
        'velorine-milestone-pop'
      );
    }, 1550);
  }

  #scheduleRewardSync(delay = 250) {
    if (this.#rewardSyncTimer) {
      clearTimeout(this.#rewardSyncTimer);
    }

    if (this.#confettiTimer) {
      clearTimeout(this.#confettiTimer);
    }

    this.#rewardSyncTimer = setTimeout(
      () => this.#syncVelorineRewards(),
      delay
    );
  }

  async #getCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to load cart'
      );
    }

    return response.json();
  }

  async #getGiftVariantId(handle) {
    const response = await fetch(
      `${window.Shopify.routes.root}products/${handle}.js`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
      }
    );

    if (!response.ok) {
      throw new Error(
        `Gift product not found: ${handle}`
      );
    }

    const product =
      await response.json();

    const variant =
      product.variants?.find(
        (item) => item.available
      ) ||
      product.variants?.[0];

    if (!variant) {
      throw new Error(
        `No variant found for ${handle}`
      );
    }

    return variant.id;
  }

  async #changeLine(lineKey, quantity) {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/change.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type':
            'application/json',
          Accept:
            'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to update reward gift'
      );
    }

    return response.json();
  }

  async #addGift(variantId) {
    const response = await fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type':
            'application/json',
          Accept:
            'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                _velorine_free_gift:
                  'true',
              },
            },
          ],
        }),
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to add reward gift'
      );
    }

    return response.json();
  }

  async #renderCartDrawer() {
    await sectionRenderer.renderSection(
      'cart-drawer-section',
      {
        cache: false,
        mode: 'hydration',
      }
    );

    requestAnimationFrame(() =>
      this.#updateStickyState()
    );
  }

  #getRewardForTotal(total) {
    return (
      VELORINE_REWARDS.find((reward) => {
        if (total < reward.min) {
          return false;
        }

        return (
          reward.max === undefined ||
          total <= reward.max
        );
      }) || null
    );
  }

  async #syncVelorineRewards() {
    if (this.#rewardSyncing) {
      return;
    }

    this.#rewardSyncing = true;

    try {
      let cart =
        await this.#getCart();

      /*
       * IMPORTANT:
       *
       * Only automatically generated gift
       * lines are excluded from the total.
       *
       * Manually purchased reward products
       * remain normal paid products.
       */
      const paidItems =
        cart.items.filter(
          (item) =>
            !this.#isRewardLine(item)
        );

      const qualifyingTotal =
        paidItems.reduce(
          (total, item) =>
            total +
            (item.final_line_price || 0),
          0
        );

      this.#triggerMilestoneConfetti(
        qualifyingTotal
      );

      const reward =
        this.#getRewardForTotal(
          qualifyingTotal
        );

      const requiredHandle =
        reward?.handle || null;

      let changed = false;

      /*
       * ONLY automatic gift lines are managed here.
       *
       * Manually purchased free-chain,
       * free-earcuffs, and free-ring are
       * completely ignored by this block.
       */
      const currentRewardLines =
        cart.items.filter(
          (item) =>
            this.#isRewardLine(item)
        );

      /*
       * Keep exactly one automatic reward.
       */
      for (
        const item of currentRewardLines
      ) {
        const keepThisLine =
          requiredHandle &&
          item.handle ===
            requiredHandle;

        if (
          !keepThisLine ||
          item.quantity !== 1
        ) {
          await this.#changeLine(
            item.key,
            keepThisLine ? 1 : 0
          );

          changed = true;
        }
      }

      if (changed) {
        cart =
          await this.#getCart();
      }

      /*
       * Add the required automatic gift
       * if the qualifying amount has reached
       * a gift threshold.
       */
      if (requiredHandle) {
        const hasRequiredGift =
          cart.items.some(
            (item) =>
              this.#isRewardLine(item) &&
              item.handle ===
                requiredHandle
          );

        if (!hasRequiredGift) {
          const variantId =
            await this.#getGiftVariantId(
              requiredHandle
            );

          await this.#addGift(
            variantId
          );

          changed = true;
        }
      }

      if (changed) {
        await this.#renderCartDrawer();
      }

    } catch (error) {
      console.warn(
        '[Velorine Rewards]',
        error
      );
    } finally {
      this.#rewardSyncing = false;
    }
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}